/**
 * Custom hook for navigation that automatically preserves global URL parameters
 * Wraps React Router's useNavigate with automatic range parameter preservation
 */

import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTimeRange } from '../contexts/TimeRangeContext';
import { buildUrlWithRange } from '../utils/navigation';

/**
 * Hook that returns a navigate function which automatically preserves the range parameter
 * @returns {Function} navigate function that preserves global params
 */
export const useNavigateWithRange = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedRange } = useTimeRange();

  /**
   * Navigate to a path while preserving the range parameter
   * @param {string} path - Target path (e.g., '/traces', '/agents')
   * @param {Object} params - Additional query parameters to include
   * @param {Object} options - React Router navigate options (e.g., { replace: true, state: {...} })
   */
  const navigateWithRange = (path, params = {}, options = {}) => {
    if (!path) throw new Error('navigateWithRange requires path');

    const url = buildUrlWithRange(path, params, searchParams, selectedRange);
    navigate(url, options);
  };

  return navigateWithRange;
};
